import { isLocalStorageAvailable, isSessionStorageAvailable } from "@/lib/utils/browser-detection";

export const REMEMBER_ME_ENABLED_KEY = "rakb.remember.enabled";
export const REMEMBER_ME_EMAIL_KEY = "rakb.remember.email";

export const getRememberMeEnabled = (): boolean => {
  if (!isLocalStorageAvailable()) {
    return true;
  }
  const stored = localStorage.getItem(REMEMBER_ME_ENABLED_KEY);
  return stored === null ? true : stored === "true";
};

export const setRememberMeEnabled = (enabled: boolean): void => {
  if (!isLocalStorageAvailable()) {
    return;
  }
  localStorage.setItem(REMEMBER_ME_ENABLED_KEY, enabled ? "true" : "false");
};

export const loadRememberedEmail = (): string => {
  if (!getRememberMeEnabled() || !isLocalStorageAvailable()) {
    return "";
  }
  return localStorage.getItem(REMEMBER_ME_EMAIL_KEY) ?? "";
};

export const saveRememberedEmail = (email: string): void => {
  if (!isLocalStorageAvailable()) {
    return;
  }
  const normalized = email.trim().toLowerCase();
  if (!normalized) {
    return;
  }
  localStorage.setItem(REMEMBER_ME_EMAIL_KEY, normalized);
  localStorage.setItem(REMEMBER_ME_ENABLED_KEY, "true");
};

export const clearRememberedCredentials = (): void => {
  if (!isLocalStorageAvailable()) {
    return;
  }
  localStorage.removeItem(REMEMBER_ME_EMAIL_KEY);
  localStorage.setItem(REMEMBER_ME_ENABLED_KEY, "false");
};

const pickAuthStorage = (): Storage | null => {
  if (getRememberMeEnabled()) {
    return isLocalStorageAvailable() ? window.localStorage : null;
  }
  return isSessionStorageAvailable() ? window.sessionStorage : null;
};

/**
 * Supabase auth storage: localStorage when "remember me" is on, sessionStorage otherwise.
 */
export const supabaseAuthStorage = {
  getItem: (key: string): string | null => {
    const primary = pickAuthStorage();
    const fromPrimary = primary?.getItem(key) ?? null;
    if (fromPrimary !== null) {
      return fromPrimary;
    }
    if (isLocalStorageAvailable()) {
      const fromLocal = localStorage.getItem(key);
      if (fromLocal !== null) {
        return fromLocal;
      }
    }
    if (isSessionStorageAvailable()) {
      return sessionStorage.getItem(key);
    }
    return null;
  },
  setItem: (key: string, value: string): void => {
    const target = pickAuthStorage();
    if (!target) {
      return;
    }
    target.setItem(key, value);
    const other = target === window.localStorage ? window.sessionStorage : window.localStorage;
    if (other) {
      other.removeItem(key);
    }
  },
  removeItem: (key: string): void => {
    if (isLocalStorageAvailable()) {
      localStorage.removeItem(key);
    }
    if (isSessionStorageAvailable()) {
      sessionStorage.removeItem(key);
    }
  },
};
